import { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { IncidentFilters, IncidentsListTable } from '../organism';
import { IncidentFilterInput, IncidentStatusOption, OrderDirectionOption, PageSizeOption, useGetIncidentsForListQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const initialFilters: IncidentFilterInput = {
  includeStatusOption: null,
  includeStatusOptions: null,
  excludeStatusOption: null,
  excludeStatusOptions: [IncidentStatusOption.Completed],
  countryId: null,
  countryIds: null,
  stateId: null,
  stateIds: null,
  cityId: null,
  cityIds: null,
  typeOption: null,
  categoryOption: null,
  priorityOption: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  search: null,
  page: null,
};

const textsData = {
  en: {
    title: 'Incidents',
    buttonText: '+ Add Incident'
  },
  es: {
    title: 'Incidentes',
    buttonText: '+ Agregar Incidente'
  }
}

const normalizeIncidentFilters = (input: IncidentFilterInput): IncidentFilterInput => {
  const includeFromSingle = input.includeStatusOption ? [input.includeStatusOption] : null
  const excludeFromSingle = input.excludeStatusOption ? [input.excludeStatusOption] : null
  const countryFromSingle = input.countryId ? [input.countryId] : null
  const stateFromSingle = input.stateId ? [input.stateId] : null
  const cityFromSingle = input.cityId ? [input.cityId] : null

  return {
    ...input,
    includeStatusOptions: input.includeStatusOptions?.length ? input.includeStatusOptions : includeFromSingle,
    excludeStatusOptions: input.excludeStatusOptions?.length ? input.excludeStatusOptions : excludeFromSingle,
    includeStatusOption: null,
    excludeStatusOption: null,
    countryIds: input.countryIds?.length ? input.countryIds : countryFromSingle,
    stateIds: input.stateIds?.length ? input.stateIds : stateFromSingle,
    cityIds: input.cityIds?.length ? input.cityIds : cityFromSingle,
    countryId: null,
    stateId: null,
    cityId: null,
  }
}

export const IncidentListPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('incidents')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(normalizeIncidentFilters(savedState.filters as IncidentFilterInput))
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('incidents')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<IncidentFilterInput>(() => {
    const savedState = getListState('incidents')
    return savedState?.filters
      ? normalizeIncidentFilters(savedState.filters as IncidentFilterInput)
      : initialFilters
  });

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('incidents', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: IncidentFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const { data } = useGetIncidentsForListQuery({
    variables: {
      input: { ...filters, page: currentPage.toString() },
    },
    fetchPolicy: 'network-only',
  });

  const incidents = data?.incidents;
  const isPaginated = incidents && incidents.__typename === 'IncidentsPaginatedOutput';

  const currentDataCount =
    isPaginated && 'items' in incidents ? incidents.items.length : 0;
  const totalPages =
    isPaginated && 'pagination' in incidents ? incidents.pagination.totalPages : 1;
  const showPagination = isPaginated && currentDataCount > 0;

  return (
    <div className="w-full max-w-none space-y-4 px-4 pb-4 pt-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <h2 className="text-xl font-semibold text-gray-900">{texts.title}</h2>
        <div className="flex w-full justify-end sm:ml-auto sm:w-auto">
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </div>
      </div>

      <div>
        <IncidentFilters filters={filters} onFilterChange={handleFilterChange} />
      </div>

      <div>
        <IncidentsListTable filters={{ ...filters, page: currentPage.toString() }} />
      </div>

      {showPagination && (
        <div>
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            currentDataCount={currentDataCount}
            pageSize={filters.pageSize || null}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  )
}

